import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { Toaster } from "./components/ui/sonner";
import "./i18n";
import { syncLanguageWithUser } from "./i18n";
import LandingPage from "./pages/LandingPage";
import LandingPageB from "./pages/LandingPageB";
import LandingPageW from "./pages/LandingPageW";
import PublicCirclePage from "./pages/PublicCirclePage";
import PublicGratitudeWallPage from "./pages/PublicGratitudeWallPage";
import PublicProfilePage from "./pages/PublicProfilePage";
import GratitudeRespondPage from "./pages/GratitudeRespondPage";
import Dashboard from "./pages/Dashboard";
import SwotTool from "./pages/SwotTool";
import CoreValuesTool from "./pages/CoreValuesTool";
import NetworkPage from "./pages/NetworkPage";
import NotificationsPage from "./pages/NotificationsPage";
import ProfilePage from "./pages/ProfilePage";
import GiveInsightPage from "./pages/GiveInsightPage";
import ViewInsightPage from "./pages/ViewInsightPage";
import InvitePage from "./pages/InvitePage";
import MyselfPage from "./pages/MyselfPage";
import SelfPortraitPage from "./pages/SelfPortraitPage";
import AdminDashboard from "./pages/AdminDashboard";
import InsightsSummaryPage from "./pages/InsightsSummaryPage";
import MobileAppBanner from "./components/MobileAppBanner";
import WheelOfLifePage from "./pages/WheelOfLifePage";
import GratitudeWallPage from "./pages/GratitudeWallPage";
import PrivacyPage from "./pages/PrivacyPage";
import SACSAAEPage from "./pages/SACSAAEPage";
import AppLayout from "./components/AppLayout";
import PublicAppLayout from "./components/PublicAppLayout";
import "./App.css";
import { identifyUser, trackLogin, trackLogout, trackPageView } from "./utils/analytics";
import { initFBPixel, fbPageView, fbCompleteRegistration } from "./utils/fbpixel";
import { toast } from "sonner";
import { HelmetProvider } from "react-helmet-async";

const API = '/api';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    initFBPixel();
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Match i18n's active language to the server-side preference so the
        // UI shell renders in the right language on first paint.
        syncLanguageWithUser(userData);
        identifyUser(userData.user_id, { email: userData.email, name: userData.name });
        trackLogin(userData.user_id);

        // Post-login: claim pending handle reserved pre-signup via the
        // public "Reserve your handle" flow. No-op if the user already has
        // the handle or it was taken in the meantime.
        const pendingUsername = sessionStorage.getItem("pending_username");
        if (pendingUsername) {
          sessionStorage.removeItem("pending_username");
          try {
            const res = await fetch(`${API}/profile/username`, {
              method: "PUT",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ username: pendingUsername }),
            });
            if (res.ok) {
              const d = await res.json();
              setUser((u) => (u ? { ...u, username: d.username } : u));
            }
          } catch {
            /* silent — user can change handle in Settings */
          }
        }

        // Post-login: migrate guest wheel data if present
        const guestWheel = localStorage.getItem("evoluras_guest_wheel");
        if (guestWheel) {
          try {
            const parsed = JSON.parse(guestWheel);
            const areas = Object.entries(parsed.scores || {}).map(([key, score]) => ({
              key,
              score,
              why_score: parsed.reflections?.[key]?.why_score || [],
              what_to_improve: parsed.reflections?.[key]?.what_to_improve || [],
              action_steps: parsed.reflections?.[key]?.action_steps || [],
            }));
            // Also include unscored active areas
            for (const k of (parsed.activeKeys || [])) {
              if (!areas.find(a => a.key === k)) {
                areas.push({ key: k, score: null, why_score: [], what_to_improve: [], action_steps: [] });
              }
            }
            if (areas.length > 0) {
              const migRes = await fetch(`${API}/tools/wheel`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ areas, focus_areas: parsed.focusKeys || [] }),
              });
              if (migRes.ok) {
                toast.success(`Welcome to Evoluras, ${userData.name?.split(" ")[0] || ""}! Your Wheel of Life has been saved.`, { duration: 5000 });
              }
            }
            localStorage.removeItem("evoluras_guest_wheel");
          } catch { /* ignore migration errors */ }
        }

        // Post-login: migrate guest values data if present
        const guestValues = localStorage.getItem("evoluras_guest_values");
        if (guestValues) {
          try {
            const parsed = JSON.parse(guestValues);
            const values = (parsed.values || []).map(v => ({
              name: v.name,
              description: v.description || v.definition || "",
              definition: v.definition || "",
              category: v.category || "",
            }));
            const antiValues = (parsed.anti_values || []).map(v => ({
              name: v.name,
              description: v.description || v.definition || "",
              definition: v.definition || "",
              category: v.category || "",
            }));
            if (values.length > 0 || antiValues.length > 0) {
              const migRes = await fetch(`${API}/tools/values/pyramid`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ values, anti_values: antiValues }),
              });
              if (migRes.ok) {
                toast.success(`Your Core Values pyramid has been saved!`, { duration: 5000 });
              }
            }
            localStorage.removeItem("evoluras_guest_values");
          } catch { /* ignore migration errors */ }
        }

        // Post-login: migrate guest SWOT data if present
        const guestSwot = localStorage.getItem("evoluras_guest_swot");
        if (guestSwot) {
          try {
            const parsed = JSON.parse(guestSwot);
            const hasItems = ["strengths","weaknesses","opportunities","threats"].some(
              cat => (parsed[cat] || []).length > 0
            );
            if (hasItems) {
              const payload = {};
              for (const cat of ["strengths","weaknesses","opportunities","threats"]) {
                payload[cat] = (parsed[cat] || []).map(i => ({ content: i.content || "" }));
              }
              const migRes = await fetch(`${API}/tools/swot/bulk`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(payload),
              });
              if (migRes.ok) {
                toast.success(`Your SWOT analysis has been saved!`, { duration: 5000 });
              }
            }
            localStorage.removeItem("evoluras_guest_swot");
          } catch { /* ignore migration errors */ }
        }

        // Post-signup viral loop: redirect to SWOT with auto-open insight request
        const postSignupAction = sessionStorage.getItem("evoluras_post_signup_action");
        if (postSignupAction === "request_swot_insight") {
          sessionStorage.removeItem("evoluras_post_signup_action");
          sessionStorage.setItem("evoluras_auto_open_insight", "swot");
        }

        // Track registration/lead via FB if this is a new session from guest
        if (guestWheel || guestValues || guestSwot) {
          fbCompleteRegistration("google_oauth");
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Global fetch wrapper: clears user on 401 so ProtectedRoute redirects to login
  const authFetch = useCallback(async (url, options = {}) => {
    const response = await fetch(url, { credentials: 'include', ...options });
    if (response.status === 401) {
      setUser(null);
    }
    return response;
  }, []);

  const logout = async () => {
    trackLogout();
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, authFetch }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    // Store intended destination (including query string) so login can redirect back
    const returnPath = `${location.pathname}${location.search || ''}`;
    sessionStorage.setItem('pending_return_url', returnPath);
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// Public routes that should wear the AppLayout (sidebar + top bar) when
// a user is signed in, and the PublicAppLayout (guest sidebar + auth CTAs)
// when they're not. Ensures visitors always have a clear path to sign up
// and the in-app nav is preserved for signed-in users crossing into
// public surfaces.
const PublicWithOptionalLayout = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  return user ? (
    <AppLayout>{children}</AppLayout>
  ) : (
    <PublicAppLayout>{children}</PublicAppLayout>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  useEffect(() => {
    const pageMap = {
      '/': 'Landing', '/dashboard': 'Dashboard', '/myself': 'Myself',
      '/tools/swot': 'SWOT', '/tools/values': 'Core Values', '/tools/wheel': 'Wheel of Life',
      '/wheel': 'Wheel of Life (Guest)',
      '/network': 'Network', '/notifications': 'Notifications', '/profile': 'Profile',
    };
    const pageName = pageMap[location.pathname] || location.pathname;
    trackPageView(pageName);
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/welcome" element={<LandingPageB />} />
      <Route path="/w" element={<LandingPageW />} />
      <Route path="/privacy" element={
        <PublicWithOptionalLayout><PrivacyPage /></PublicWithOptionalLayout>
      } />
      <Route path="/sacsaae" element={
        <PublicWithOptionalLayout><SACSAAEPage /></PublicWithOptionalLayout>
      } />
      <Route path="/u/:slug/circle" element={
        <PublicWithOptionalLayout><PublicCirclePage /></PublicWithOptionalLayout>
      } />
      <Route path="/u/:slug/gratitude" element={
        <PublicWithOptionalLayout><PublicGratitudeWallPage /></PublicWithOptionalLayout>
      } />
      <Route path="/respond/:token" element={<GratitudeRespondPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout><Dashboard /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/tools/swot" element={
        <ProtectedRoute>
          <AppLayout><SwotTool /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/myself" element={
        <ProtectedRoute>
          <AppLayout><MyselfPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/myself/portrait" element={
        <ProtectedRoute>
          <AppLayout><SelfPortraitPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/tools/values" element={
        <ProtectedRoute>
          <AppLayout><CoreValuesTool /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/network" element={
        <ProtectedRoute>
          <AppLayout><NetworkPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <AppLayout><NotificationsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <AppLayout><ProfilePage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/insights/pending" element={
        <ProtectedRoute>
          <Navigate to="/dashboard" replace />
        </ProtectedRoute>
      } />
      <Route path="/insights/give/:requestId" element={
        <ProtectedRoute>
          <AppLayout><GiveInsightPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/insights/view/:requestId" element={
        <ProtectedRoute>
          <AppLayout><ViewInsightPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/insights/summary" element={
        <ProtectedRoute>
          <AppLayout><InsightsSummaryPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/wheel" element={<PublicWithOptionalLayout><WheelOfLifePage guest={true} /></PublicWithOptionalLayout>} />
      <Route path="/values" element={<PublicWithOptionalLayout><CoreValuesTool guest={true} /></PublicWithOptionalLayout>} />
      <Route path="/swot" element={<PublicWithOptionalLayout><SwotTool guest={true} /></PublicWithOptionalLayout>} />
      <Route path="/tools/wheel" element={
        <ProtectedRoute>
          <AppLayout><WheelOfLifePage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/tools/gratitude" element={
        <ProtectedRoute>
          <AppLayout><GratitudeWallPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AppLayout><AdminDashboard /></AppLayout>
        </ProtectedRoute>
      } />
      {/*
        Public @username profile — MUST stay last so it doesn't swallow
        app routes. Reserved words are rejected at profile creation time
        (see utils/username.py RESERVED), so this is safe as a catch-all.
      */}
      <Route path="/:username/gratitude" element={
        <PublicWithOptionalLayout><PublicGratitudeWallPage /></PublicWithOptionalLayout>
      } />
      <Route path="/:username" element={
        <PublicWithOptionalLayout><PublicProfilePage /></PublicWithOptionalLayout>
      } />
    </Routes>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <MobileAppBanner />
          <AppRouter />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
